import React, { useCallback, useEffect, useRef, useState } from 'react';

type ScratchRevealProps = {
  /** Content revealed under the scratch layer */
  children: React.ReactNode;
  /** Cover colour (gold sand default) */
  coverColor?: string;
  /** Optional foil / sand texture image drawn under the speckles */
  coverImageUrl?: string;
  /** Fraction of pixels cleared before auto-reveal (0–1) */
  revealThreshold?: number;
  className?: string;
  height?: number;
  /** Hint copy on the foil */
  hint?: string;
  /** Fired once when the cover clears past the threshold */
  onReveal?: () => void;
};

/**
 * Canvas scratch-to-reveal (gold foil / sand) — ShaadiPath / WeddingWaliDidi style.
 * Pointer + touch friendly; ResizeObserver for card-preview widths.
 */
export function ScratchReveal({
  children,
  coverColor = '#C9A227',
  coverImageUrl,
  revealThreshold = 0.38,
  className = '',
  height = 320,
  hint = 'Scratch the gold foil',
  onReveal,
}: ScratchRevealProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const drawing = useRef(false);
  const revealedRef = useRef(false);
  const onRevealRef = useRef(onReveal);
  onRevealRef.current = onReveal;
  const [revealed, setRevealed] = useState(false);

  const paintCover = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    if (rect.width < 8) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const finishPaint = () => {
      // Speckle “sand / leaf flakes”
      for (let i = 0; i < 220; i++) {
        ctx.fillStyle = `rgba(255,255,255,${0.05 + Math.random() * 0.18})`;
        ctx.beginPath();
        ctx.arc(Math.random() * rect.width, Math.random() * height, Math.random() * 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
      for (let i = 0; i < 40; i++) {
        ctx.fillStyle = `rgba(80,50,10,${0.08 + Math.random() * 0.15})`;
        ctx.fillRect(Math.random() * rect.width, Math.random() * height, 3 + Math.random() * 8, 1);
      }
      ctx.fillStyle = 'rgba(40,28,8,0.72)';
      ctx.font = '700 12px "Libre Baskerville", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText(hint.toUpperCase(), rect.width / 2, height / 2 - 6);
      ctx.font = '400 11px "Source Sans 3", system-ui, sans-serif';
      ctx.fillStyle = 'rgba(40,28,8,0.5)';
      ctx.fillText('Swipe with your finger', rect.width / 2, height / 2 + 14);
    };

    if (coverImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, height);
        // Warm foil wash so text stays readable
        const wash = ctx.createLinearGradient(0, 0, rect.width, height);
        wash.addColorStop(0, 'rgba(201,162,39,0.35)');
        wash.addColorStop(1, 'rgba(166,124,0,0.45)');
        ctx.fillStyle = wash;
        ctx.fillRect(0, 0, rect.width, height);
        finishPaint();
      };
      img.onerror = () => {
        const g = ctx.createLinearGradient(0, 0, rect.width, height);
        g.addColorStop(0, coverColor);
        g.addColorStop(0.5, '#E8D48B');
        g.addColorStop(1, '#A67C00');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, rect.width, height);
        finishPaint();
      };
      img.src = coverImageUrl;
      return;
    }

    const g = ctx.createLinearGradient(0, 0, rect.width, height);
    g.addColorStop(0, coverColor);
    g.addColorStop(0.5, '#E8D48B');
    g.addColorStop(1, '#A67C00');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, rect.width, height);
    finishPaint();
  }, [coverColor, coverImageUrl, height, hint]);

  useEffect(() => {
    paintCover();
    const onResize = () => {
      if (!revealed) paintCover();
    };
    window.addEventListener('resize', onResize);
    const ro =
      typeof ResizeObserver !== 'undefined' && containerRef.current
        ? new ResizeObserver(() => {
            if (!revealed) paintCover();
          })
        : null;
    if (ro && containerRef.current) ro.observe(containerRef.current);
    const raf = window.requestAnimationFrame(() => {
      if (!revealed) paintCover();
    });
    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, [paintCover, revealed]);

  const scratchAt = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas || revealedRef.current) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 30, 0, Math.PI * 2);
    ctx.fill();

    const sample = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let clear = 0;
    const step = 16;
    for (let i = 3; i < sample.data.length; i += 4 * step) {
      if (sample.data[i] < 128) clear++;
    }
    const total = sample.data.length / (4 * step);
    if (total > 0 && clear / total >= revealThreshold) {
      revealedRef.current = true;
      setRevealed(true);
      onRevealRef.current?.();
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    drawing.current = true;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    scratchAt(e.clientX, e.clientY);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    scratchAt(e.clientX, e.clientY);
  };
  const onPointerUp = () => {
    drawing.current = false;
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden ${className}`}
      style={{ height }}
      data-testid="scratch-reveal"
    >
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
      {!revealed && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 z-10 touch-none cursor-crosshair"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          aria-label="Scratch cover to reveal the save the date"
        />
      )}
    </div>
  );
}
